<template>
  <div class="fp" data-pfe-menu-root @keydown.esc="onEscape">
    <span v-if="needsReview" class="fp-pill" :data-test="'pfe-pick-pill-' + kind">Pick a form</span>
    <button
      type="button"
      class="fp-trigger"
      :class="{ 'fp-trigger--open': open, 'fp-trigger--review': needsReview, 'fp-trigger--empty': !selectedName }"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="kind === 'postOp' ? 'Post-op form' : 'Consent form'"
      :title="label"
      :data-test="kind === 'postOp' ? 'pfe-postop-trigger' : 'pfe-consent-trigger'"
      @click="toggle"
    >
      <span class="fp-label">{{ label }}</span>
      <i class="fa fa-chevron-down fp-chev" aria-hidden="true"></i>
    </button>

    <pop-menu v-if="open" :max-height="380" :min-width="kind === 'postOp' ? 330 : 320" data-test="pfe-form-menu">
      <template #header>
        <div class="fp-search">
          <i class="fa fa-search" aria-hidden="true"></i>
          <input
            ref="search"
            v-model="query"
            type="text"
            class="fp-search-input"
            placeholder="Search forms"
            aria-label="Search forms"
            data-test="pfe-form-search"
          />
        </div>
      </template>

      <button
        v-if="!query.trim()"
        type="button"
        class="fp-option fp-option--blank"
        data-test="pfe-form-none"
        @click="pick('')"
      >
        <span class="fp-option-name">{{ blankLabel }}</span>
        <i v-if="!value" class="fa fa-check fp-check" aria-hidden="true"></i>
      </button>

      <div v-if="loading && forms.length === 0" class="fp-empty" data-test="pfe-form-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading forms&hellip;
      </div>

      <div v-for="(g, gi) in menu.groups" :key="gi" class="fp-group" data-test="pfe-form-group">
        <div v-if="g.heading" class="fp-heading">{{ g.heading }}</div>
        <button
          v-for="f in g.forms"
          :key="f.formIDForWeb"
          type="button"
          class="fp-option"
          :class="{ 'fp-option--on': f.formIDForWeb === value }"
          data-test="pfe-form-option"
          @click="pick(f.formIDForWeb)"
        >
          <span class="fp-option-name">{{ f.formName }}</span>
          <i v-if="f.formIDForWeb === value" class="fa fa-check fp-check" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="menu.none && !loading" class="fp-empty" data-test="pfe-form-empty">
        <template v-if="query.trim()">No forms match &ldquo;{{ query.trim() }}&rdquo;.</template>
        <template v-else>No forms yet. Create one in Forms first.</template>
      </div>

      <template v-if="menu.showAllLink" #footer>
        <button type="button" class="fp-showall" data-test="pfe-show-all" @click="showAll = true">
          Show all forms<span class="fp-showall-n">&middot; {{ menu.otherCount }} more</span>
          <i class="fa fa-chevron-down fp-showall-chev" aria-hidden="true"></i>
        </button>
      </template>
    </pop-menu>
  </div>
</template>

<script>
import PopMenu from './PopMenu.vue';
import { formMenuGroups, formNameFor } from '@/utils/postOp/rows';

/**
 * One form dropdown cell — the consent variant or the post-op variant (design: "Consent form · optional" /
 * "Post-op form · optional"). `value` is a formIDForWeb ('' = none). Picking — even "no form" — answers the seeder's
 * amber "Pick a form" flag (the parent clears `review` in rows.js withConsentForm / withPostOpForm).
 */
export default {
  name: 'PfeFormPicker',
  components: { PopMenu },
  props: {
    kind: { type: String, default: 'consent', validator: (v) => v === 'consent' || v === 'postOp' },
    value: { type: String, default: '' },
    forms: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    needsReview: { type: Boolean, default: false },
  },
  data() {
    return { open: false, query: '', showAll: false };
  },
  computed: {
    blankLabel() { return this.kind === 'postOp' ? 'No post-op form' : 'No consent form'; },
    selectedName() { return this.value ? formNameFor(this.forms, this.value) : ''; },
    /** What the closed cell says. A stored id the list does not know (deleted form, or still loading) is said, not hidden. */
    label() {
      if (!this.value) return this.needsReview ? 'Choose a form' : this.blankLabel;
      if (this.selectedName) return this.selectedName;
      return this.loading ? 'Loading…' : 'Form not found';
    },
    menu() { return formMenuGroups(this.forms, this.kind, this.query, this.showAll); },
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutside, true);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside, true);
  },
  methods: {
    toggle() {
      if (this.open) { this.open = false; return; }
      this.open = true;
      this.query = '';
      this.showAll = false;
      this.$nextTick(() => { if (this.$refs.search) this.$refs.search.focus(); });
    },
    close() { this.open = false; },
    onOutside(e) {
      if (this.open && this.$el && !this.$el.contains(e.target)) this.close();
    },
    onEscape(e) {
      if (!this.open) return;
      e.preventDefault();
      e.stopPropagation();
      this.close();
    },
    pick(formID) {
      this.open = false;
      this.$emit('input', formID);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.fp { position: relative; min-width: 0; padding-top: 27px; }

.fp-pill {
  position: absolute;
  top: 19px;
  left: 10px;
  z-index: 1;
  padding: 0 7px;
  border-radius: 999px;
  background: $pp-amber-subtle;
  border: 1px solid $pp-amber;
  color: $pfe-amber-ink;
  font-size: 11px;
  font-weight: 800;
  line-height: 16px;
  white-space: nowrap;
}

.fp-trigger {
  @include pfe-field(42px);
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 12px;
  text-align: left;
  cursor: pointer;

  &--empty { color: $pfe-neutral-400; }
  &--open { @include pfe-focus-ring; border-width: 1.5px; }
  &--review {
    border: 1.5px solid $pp-amber;
    background: $pp-amber-subtle;
    color: $pfe-amber-ink;
  }
  &:focus-visible { @include pfe-focus-ring; }
}
.fp-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fp-chev { flex: none; font-size: 12px; color: $pfe-neutral-400; }

.fp-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 6px;
  color: $pfe-neutral-400;
}
.fp-search-input {
  flex: 1;
  min-width: 0;
  height: 34px;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: $pp-text;

  &:focus { outline: none; }
}
.fp-group { padding-top: 4px; }
.fp-heading {
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pfe-neutral-500;
}
.fp-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  color: $pp-text;
  cursor: pointer;

  &:hover, &:focus-visible { background: $pfe-neutral-50; outline: none; }
  &--on { font-weight: 700; }
  &--blank { color: $pfe-neutral-500; }
}
.fp-option-name { flex: 1; min-width: 0; }
.fp-check { color: $pp-brand; font-size: 13px; }
.fp-empty { padding: 14px 10px; font-size: 13.5px; color: $pfe-neutral-500; }
.fp-showall {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 11px 16px;
  border: 0;
  border-top: 1px solid $pp-border;
  background: $pfe-neutral-25;
  font: inherit;
  font-size: 13.5px;
  font-weight: 700;
  text-align: left;
  color: $pp-brand;
  cursor: pointer;

  &:hover { background: $pfe-neutral-50; }
}
.fp-showall-n { font-weight: 500; color: $pfe-neutral-500; }
.fp-showall-chev { margin-left: auto; font-size: 12px; }
</style>
